import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Play, Pause, Scissors, Plus, Trash2, Zap, Loader2,
  CheckCircle2, AlertTriangle, Download, FileSpreadsheet, FileJson, Film, Package,
  ChevronDown, ChevronUp, Tag, X as XIcon, Save, Sparkles, ArrowRightCircle,
} from "lucide-react";import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label as UILabel } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import ClipPassVectorEditor from "@/components/ClipPassVectorEditor";
import PassVectorTriage from "@/components/PassVectorTriage";
import SpielerVerfolger from "@/components/wizard/SpielerVerfolger";
import ReidStitchButton from "@/components/triage/ReidStitchButton";
import CameraCoverageBar from "@/components/wizard/CameraCoverageBar";
import {
  videoUrl, listClips, createClip, deleteClip, startClipTracking,
  getClipJob, setClipLabels, clipExportUrl, updateClip, downloadFile,
  rerenderClipMp4,
  fehlerText,
} from "@/lib/api";
import { SPLITS_BY_FORMAT } from "@/lib/gameConfig";
import { TID } from "@/lib/testIds";
import { zahl, tempo } from "@/lib/trackingSummary";
import ParamHelp from "@/components/ParamHelp";

const OFFENSE = ["Vertical Stack", "Horizontal Stack", "Side Stack", "Iso", "Split Stack", "German"];
const DEFENSE = ["Man", "Zone (Cup)", "Zone (Clam)", "Junk", "Switch", "Force Flick", "Force Backhand"];
const PHASE = ["Pull Play", "Reset", "Endzone", "Transition", "Counter-Attack", "Stalled"];
const QUALITY = ["Low", "Medium", "High", "Reference"];
const DIFFICULTY = ["Easy", "Medium", "Hard"];

const MAX_CLIP = 60;
const fmtTime = (s) => {
  const v = Math.max(0, s || 0);
  const m = Math.floor(v / 60);
  const sec = (v % 60).toFixed(2);
  return `${m}:${sec.padStart(5, "0")}`;
};

export default function SequenceLab({ sequenceId, sequence, onFinish }) {
  const videoRef = useRef(null);
  const duration = sequence?.duration_s || 30;
  const isMixed = sequence?.game_config?.gender === "Mixed";

  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  // 🆕 05.09. · Spieler-Verfolgungs-Modus (Gold-Spuren per Maus)
  const [verfolgen, setVerfolgen] = useState(false);
  const [inS, setInS] = useState(0);
  const [outS, setOutS] = useState(Math.min(15, duration));
  const [clips, setClips] = useState([]);
  const [creating, setCreating] = useState(false);
  const [expandedClip, setExpandedClip] = useState(null);
  // The scrubber overlays a thin pink tick per heuristic-proposed pass
  // across all clips in the sequence — gives the coach a "where do the
  // plays happen?" overview at a single glance, and the tick itself is
  // clickable for one-tap navigation to a pass moment.
  const passMarkers = useMemo(() => {
    const out = [];
    for (const c of clips) {
      for (const v of (c.pass_vectors || [])) {
        if (typeof v.timestamp_seconds !== "number") continue;
        const t = (c.start_s || 0) + v.timestamp_seconds;
        if (t < 0 || t > duration) continue;
        out.push({
          id: `${c.id}:${v.id}`,
          t,
          status: v.status,
          title: `Pass #${v.thrower_player_id ?? "?"}→#${v.catcher_player_id ?? "?"} · ${(v.length_m || 0).toFixed(1)} m`,
        });
      }
    }
    return out;
  }, [clips, duration]);

  // When a clip row is expanded, the timeline ZOOMS to that clip's
  // [start_s, end_s] window — the markers (each living in a tiny 0.6%
  // slice of a 41-minute video) suddenly spread visibly over the
  // 1920 px scrubber, making click-to-seek genuinely precise. When no
  // clip is expanded we fall back to the full-match view.
  const expandedClipObj = useMemo(
    () => clips.find((c) => c.id === expandedClip) || null,
    [clips, expandedClip],
  );
  const scrubWindow = useMemo(() => {
    if (expandedClipObj && typeof expandedClipObj.start_s === "number"
        && typeof expandedClipObj.end_s === "number"
        && expandedClipObj.end_s > expandedClipObj.start_s) {
      return {
        start: expandedClipObj.start_s,
        end: expandedClipObj.end_s,
        label: expandedClipObj.name || `Clip ${(expandedClipObj.id || "").slice(0, 6)}`,
        zoomed: true,
      };
    }
    return { start: 0, end: duration, label: null, zoomed: false };
  }, [expandedClipObj, duration]);

  const refresh = useCallback(async () => {
    if (!sequenceId) return;
    const list = await listClips(sequenceId);
    setClips(list);
  }, [sequenceId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Deep-link from the Dashboard Flow-Map: `/admin/wizard/<seqId>?clip=<id>`
  // ── Deep-link handler · `/admin/wizard/SEQ?clip=CLIP_ID`
  // → auto-expand that clip so the admin lands directly on its annotation
  // form (and the Pass-Vector quick-toggle in the row header). One-shot —
  // we only honour the param on the first load so navigating around inside
  // the wizard doesn't keep yanking the user back to the deep-linked clip.
  //
  // 🐛 HOTFIX 2026-02-24-c · "Disc Annotation Burst" UX block:
  // When the URL has NO `?clip=` (e.g. clicking "EDIT PASSES" from the
  // Sequences/Library page → `/admin/wizard/SEQ_ID`), we previously
  // left `expandedClip` null and the auto-seek effect never fired —
  // the coach landed on the static calibration frame (0.0s) of the
  // FULL match, with no obvious way to find the play they wanted to
  // annotate. Now we auto-expand the FIRST clip on cold load so the
  // common case (a sequence with a single 0-candidates clip waiting
  // for a manual disc-bbox burst) immediately positions the playhead
  // at that clip's `start_s`. Uses a useRef guard so a SUBSEQUENT
  // manual collapse (`expandedClip = null`) doesn't re-trigger the
  // expansion — only the very first mount where clips become non-empty.
  const [searchParams, setSearchParams] = useSearchParams();
  const didColdLoadExpand = useRef(false);
  useEffect(() => {
    if (clips.length === 0) return;
    const target = searchParams.get("clip");
    if (target) {
      if (!clips.some((c) => c.id === target)) return;
      setExpandedClip(target);
      didColdLoadExpand.current = true;   // honoured the deep-link
      const next = new URLSearchParams(searchParams);
      next.delete("clip");
      setSearchParams(next, { replace: true });
      return;
    }
    if (!didColdLoadExpand.current) {
      // First time we see a non-empty clip list AND no deep-link →
      // land the coach on the first clip. Subsequent manual collapses
      // stick because we flip the ref immediately.
      didColdLoadExpand.current = true;
      setExpandedClip(clips[0].id);
    }
  }, [clips, searchParams, setSearchParams]);

  // Video bindings
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) v.pause(); else v.play();
  };
  const seek = (t, { scroll = false } = {}) => {
    const v = videoRef.current;
    if (!v) return;
    const target = Math.max(0, Math.min(duration, t));
    // fastSeek snaps to the nearest keyframe — drastically faster on
    // long B2-streamed videos with sparse keyframes than `currentTime`,
    // which forces the decoder to render the exact frame on every nudge.
    // Slider drags + the ±1s / ±5s buttons feel ~instant with this; the
    // Set-In/Out actions read `time` (state) so frame-exact accuracy is
    // unaffected. Falls back to `currentTime` on Chromium (no fastSeek).
    if (typeof v.fastSeek === "function") {
      v.fastSeek(target);
    } else {
      v.currentTime = target;
    }
    // Nudge the React `time` state directly so the timecode + scrubber
    // reflect the new position immediately instead of waiting for the
    // <video>'s `timeupdate` event (which only fires while playing).
    setTime(target);
    // Deep-link from the Triage row → scroll the main video into view
    // so the coach can immediately see the release frame instead of
    // the calibration frame the inline re-draw shows.
    if (scroll) {
      try { v.scrollIntoView({ behavior: "smooth", block: "start" }); }
      catch { /* older Safari without options arg */ v.scrollIntoView(); }
    }
  };

  // 🆕 2026-02-28 v3 · Frame-Perfect Stepping. Distinct from `seek()`
  // because that uses `v.fastSeek()` which snaps to the nearest
  // keyframe — useful for slider-drag UX but DISASTROUS for ±1-frame
  // nudging (the player would land on the same keyframe for ~30
  // consecutive nudges). Here we force the precise `v.currentTime =
  // target` path which makes the decoder render the exact frame.
  // Used by the PassVectorRedrawOverlay arrow-key handler for ML-grade
  // release-moment capture.
  const nudgeFrame = (deltaSec) => {
    const v = videoRef.current;
    if (!v) return;
    const cur = v.currentTime || 0;
    const target = Math.max(0, Math.min(duration, cur + deltaSec));
    v.currentTime = target;        // precise — bypass fastSeek keyframe-snap
    setTime(target);
  };

  // 🆕 2026-02-28 v6 · Master-player ←/→ scrubbing keyboard shortcut.
  // Coaches asked for a no-mouse way to advance the master video by a
  // coarse 1 s step while triaging — matching the muscle-memory they
  // already use in the PassVectorRedrawOverlay (frame-step). To keep
  // the two surfaces from double-stepping when an overlay is mounted,
  // we cede control to the overlay's own handler via a DOM query for
  // `[data-testid^="pass-vector-redraw-"]`. Both Create-mode AND
  // Edit-mode overlays carry that root testid (PassVectorRedrawOverlay
  // line 185), so the suppression is universal.
  //
  // Step size: 1.0 s. Frame-perfect ±1/30 stepping stays inside the
  // overlay as the precise tool; the master player is for coarse
  // triage navigation. e.preventDefault() blocks horizontal scroll on
  // pages where the wizard panel overflows.
  //
  // Empty deps `[]` is intentional: `videoRef` is a mutable ref and
  // `setTime` is a stable React setter. The listener is installed
  // once on mount and torn down on unmount — no per-render churn.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const ae = document.activeElement;
      const tag = ae?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || ae?.isContentEditable) return;
      // Pinning overlay open ⇒ let IT own the arrow keys (frame-step).
      if (document.querySelector('[data-testid^="pass-vector-redraw-"]')) return;
      const v = videoRef.current;
      if (!v) return;
      e.preventDefault();
      const delta = e.key === "ArrowRight" ? 1.0 : -1.0;
      const cur = v.currentTime || 0;
      const target = Math.max(0, cur + delta);   // browser clamps at duration
      v.currentTime = target;
      setTime(target);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // 🐛 HOTFIX 2026-02-24 · belt-and-suspenders auto-seek. When the
  // <video> finishes loading its metadata, if a clip is already
  // expanded we land the playhead inside it. Pairs with the useEffect
  // below which handles the OPPOSITE timing (clip expanded AFTER
  // video already loaded).
  const onVideoLoadedMetadata = () => {
    if (!expandedClipObj) return;
    const target = expandedClipObj.start_s;
    if (typeof target !== "number" || target <= 0) return;
    seek(target);
  };

  // 🐛 HOTFIX 2026-02-24 — when a clip is expanded (manual click OR
  // deep-link from the dashboard), force the video player to seek to
  // its `start_s`. Otherwise the canvas keeps showing frame 0 (the
  // static calibration frame at the very beginning of the match),
  // which is useless once the coach hits "Edit Passes" on a clip that
  // starts at, say, 34.82s. We only nudge the video if it's currently
  // FAR from the clip's window (≥ 0.5s outside) so playback already
  // inside the clip isn't yanked backwards. Race-safe: if the <video>
  // element hasn't finished loading metadata yet we retry every 200ms
  // for up to ~3s.
  //
  // 🐛 HOTFIX 2026-02-24-b — also OPTIMISTICALLY update the React
  // `time` state (and queue v.currentTime = target) on the very first
  // attempt, even if metadata isn't loaded yet. Chromium queues the
  // currentTime assignment until the media decodes — so the seek
  // happens automatically once playback is possible. This also makes
  // headless test envs (which can't decode B2 H.264 streams) observe
  // the seek intent in the UI's timecode display.
  useEffect(() => {
    if (!expandedClipObj) return;
    const target = expandedClipObj.start_s;
    if (typeof target !== "number" || target <= 0) return;
    // Optimistic UI: reflect the seek intent immediately so the
    // timeline + timecode show the right window even before the video
    // decodes. seek() also queues v.currentTime so the actual playhead
    // catches up automatically once metadata arrives.
    seek(target);
    let attempts = 0;
    const trySeek = () => {
      const v = videoRef.current;
      // readyState >= 1 means metadata loaded (HAVE_METADATA) — by then
      // the seek is guaranteed to have stuck. We re-issue if the player
      // is currently scrubbed OUTSIDE the clip window (e.g. coach was
      // mid-play in a different clip before the deep-link arrived).
      if (v && v.readyState >= 1 && !Number.isNaN(v.duration) && v.duration > 0) {
        const current = v.currentTime || 0;
        const inWindow = current >= target - 0.5
                       && current <= (expandedClipObj.end_s ?? target);
        if (!inWindow) seek(target);
        return true;
      }
      return false;
    };
    if (trySeek()) return;
    const interval = setInterval(() => {
      attempts += 1;
      if (trySeek() || attempts > 15) clearInterval(interval);
    }, 200);
    return () => clearInterval(interval);
  }, [expandedClipObj?.id]);

  const setIn = () => setInS(+time.toFixed(2));
  const setOut = () => setOutS(+time.toFixed(2));

  const validNew = outS > inS && (outS - inS) <= MAX_CLIP && (outS - inS) > 0.1;

  const addClip = async () => {
    if (!validNew) {
      toast.error(outS - inS > MAX_CLIP ? `Max ${MAX_CLIP}s` : "Set In before Out");
      return;
    }
    try {
      setCreating(true);
      const c = await createClip(sequenceId, { start_s: inS, end_s: outS });
      toast.success(`Created ${c.name} · ${(outS - inS).toFixed(1)}s`);
      setExpandedClip(c.id);
      await refresh();
    } catch (err) {
      toast.error(fehlerText(err) || "Could not create sequence");
    } finally {
      setCreating(false);
    }
  };

  const onClipDelete = async (id) => {
    if (!confirm("Delete this sequence?")) return;
    // Optimistic UI: drop the row immediately so the user gets feedback even
    // if the network round-trip is slow.
    setClips((prev) => prev.filter((c) => c.id !== id));
    try {
      await deleteClip(id);
      toast.success("Sequence deleted");
    } catch (err) {
      const msg = fehlerText(err) || err?.message || "Delete failed";
      toast.error(`Could not delete sequence: ${msg}`);
    } finally {
      refresh();
    }
  };

  return (
    <div data-testid={TID.wizardStep(3)} className="space-y-6">
      {/* Video player + cutter */}
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-3">
          <div className="relative border border-border rounded-sm overflow-hidden bg-black">
            <SpielerVerfolger
              sequenceId={sequenceId}
              videoRef={videoRef}
              aktiv={verfolgen}
            >
              <video
                data-testid={TID.cutterPlayer}
                ref={videoRef}
                src={videoUrl(sequenceId)}
                onTimeUpdate={() => setTime(videoRef.current?.currentTime || 0)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onLoadedMetadata={onVideoLoadedMetadata}
                preload="metadata"
                className="w-full max-h-[460px] block"
              />
            </SpielerVerfolger>
            {/* 04.09. (Nutzerwunsch): Steuerleiste NICHT mehr als Overlay
                über dem Videobild (verdeckte den unteren Bildrand),
                sondern als eigene Zeile darunter — das ganze Bild bleibt
                sichtbar. */}
            <div className="flex items-center gap-3 bg-card border-t border-border px-3 py-2">
              <Button size="sm" variant="ghost" onClick={togglePlay} className="h-8 px-2 hover:bg-accent hover:text-primary">
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </Button>
              <div className="font-mono text-xs tabular-nums">{fmtTime(time)} / {fmtTime(duration)}</div>
              <Button
                size="sm"
                variant={verfolgen ? "default" : "ghost"}
                onClick={() => setVerfolgen((v) => !v)}
                data-testid="verfolger-toggle"
                title="Spieler mit gehaltener Maustaste nachfahren — Gold-Spur für den Tracking-Vergleich"
                className="h-8 px-2 font-mono text-[10px] uppercase tracking-widest"
              >
                🎯 Verfolgen
              </Button>
              <div className="ml-auto font-mono text-[10px] uppercase tracking-widest text-primary">
                IN {fmtTime(inS)} · OUT {fmtTime(outS)} · {(outS - inS).toFixed(1)}s
              </div>
            </div>
          </div>

          {/* Prominent video timeline scrubber — full-width HTML5-style
              progress bar that lives directly under the video canvas.
              Clicking anywhere seeks; dragging the handle works too.
              The thin pink ticks mark every heuristic-proposed pass-
              vector release moment (across ALL clips in this sequence),
              so the coach can scrub from one play to the next without
              opening individual clip rows. Hover a tick → tooltip with
              thrower/catcher + distance. */}
          <VideoTimelineScrubber
            currentTime={time}
            duration={duration}
            inS={inS} outS={outS}
            markers={passMarkers}
            window={scrubWindow}
            onSeek={seek}
          />

          {/* 🆕 Kamera-Segmentierungs-Pass: stabile Kamera-Abschnitte
              als klickbare Abdeckungs-Leiste (grün = Anker, gelb = nur
              Segment-Label, umrandet = fehlt → Klick springt hin). */}
          <CameraCoverageBar
            sequenceId={sequence?.id || sequenceId}
            duration={duration}
            window={scrubWindow}
            onSeek={seek}
          />

          {/* Cut controls — relocated from the old scrub card. The
              ±1s / ±5s nudge buttons live next to the In/Out controls
              now, the bar above is the primary scrubbing UI. */}
          <div className="border border-border bg-card rounded-sm p-3 flex items-center flex-wrap gap-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mr-2">
              Nudge
            </div>
            {[-5, -1, 1, 5].map((s) => (
              <Button
                key={s} size="sm" variant="ghost"
                onClick={() => seek(time + s)}
                data-testid={`scrub-nudge-${s}`}
                className="h-7 px-2 font-mono text-[10px] hover:bg-accent hover:text-primary"
              >
                {s > 0 ? `+${s}s` : `${s}s`}
              </Button>
            ))}
            <span
              data-testid="master-player-arrow-hint"
              className="ml-2 px-1.5 py-0.5 border border-border rounded-sm text-[9px] font-mono uppercase tracking-widest text-muted-foreground"
              title="Press ←/→ to scrub the master video by 1s (no overlay open)"
            >
              ← / → · 1s
            </span>
            <div className="w-px h-5 bg-border mx-1" />
            <Button
              data-testid="set-in-button"
              size="sm" variant="outline"
              onClick={setIn}
              className="border-border bg-secondary hover:bg-accent hover:text-primary"
            >
              <Scissors className="w-3.5 h-3.5 mr-1.5" /> Set In
            </Button>
            <Button
              data-testid="set-out-button"
              size="sm" variant="outline"
              onClick={setOut}
              className="border-border bg-secondary hover:bg-accent hover:text-primary"
            >
              <Scissors className="w-3.5 h-3.5 mr-1.5 rotate-180" /> Set Out
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => seek(inS)}
              className="h-8 px-2 font-mono text-[10px] hover:bg-accent hover:text-primary"
            >
              Jump In
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => seek(outS)}
              className="h-8 px-2 font-mono text-[10px] hover:bg-accent hover:text-primary"
            >
              Jump Out
            </Button>
            <div className="ml-auto" />
            <Button
              data-testid="add-clip-button"
              onClick={addClip}
              disabled={!validNew || creating}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Sequence
            </Button>
          </div>
        </div>

        {/* Quick instructions */}
        <div className="lg:col-span-2">
          <div className="border border-border bg-card rounded-sm p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
              Sequence Lab
            </div>
            <h3 className="font-display font-black uppercase text-lg tracking-wider mb-3">
              Cut · Track · Label
            </h3>
            <ol className="space-y-2 text-sm text-muted-foreground">
              <Step n="1" t="Scrub" body="Move through the match. Use the slider and quick-jump buttons." />
              <Step n="2" t="Mark In / Out" body="Hit Set In at the start of a tactical situation, scrub forward, then Set Out." />
              <Step n="3" t="Add Sequence" body="Creates a new sequence row below — max 60s each." />
              <Step n="4" t="Track" body="Run YOLOv8x + ByteTRACK on the cut." />
              <Step n="5" t="Label" body="Fill the tactical annotation form, then export MP4/CSV/JSON." />
            </ol>
            <Button
              onClick={onFinish}
              variant="outline"
              className="w-full mt-5 border-primary/40 bg-card hover:bg-accent text-primary"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> Finish & Return
            </Button>
          </div>
        </div>
      </div>

      {/* Clips List */}
      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Sequences in this match
            </div>
            <h2 className="font-display font-black uppercase text-xl tracking-wider mt-1">
              {clips.length} {clips.length === 1 ? "Sequence" : "Sequences"}
            </h2>
          </div>
        </div>

        <NextStepHint clips={clips} />

        {clips.length === 0 ? (
          <div className="tactical-grid border border-dashed border-border rounded-sm p-10 text-center">
            <Scissors className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" strokeWidth={1.5} />
            <div className="font-display font-black uppercase tracking-widest text-foreground">
              No sequences yet
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Use the scrubber above to mark In/Out and add your first sequence.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {clips.map((c, i) => (
              <ClipRow
                key={c.id}
                clip={c}
                parent={sequence}
                index={i + 1}
                duration={duration}
                isMixed={isMixed}
                matchFormat={sequence?.game_config?.format || "Outdoor"}
                matchDivision={sequence?.game_config?.gender || "—"}
                expanded={expandedClip === c.id}
                onExpand={() => setExpandedClip(expandedClip === c.id ? null : c.id)}
                onDelete={() => onClipDelete(c.id)}
                onSeek={(t, opts) => seek(t, opts)}
                onRefresh={refresh}
                getVideoTime={() => videoRef.current?.currentTime ?? null}
                currentTime={time}
                onNudgeFrame={nudgeFrame}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * VideoTimelineScrubber · Phase A · pivot UI.
 *
 * Replaces the legacy Shadcn-Slider scrub UI with an HTML5-style
 * progress bar that:
 *
 *   • Spans the full width of the video column (no internal padding).
 *   • Renders a tall (12 px) primary-coloured fill for the played
 *     portion, an amber overlay for the current Cut window [in, out],
 *     and a circular handle the coach can drag.
 *   • Overlays a vertical pink tick per heuristic-proposed pass-vector
 *     across all clips in the sequence — instant overview of where the
 *     plays are. Each tick is independently clickable (stopPropagation)
 *     so a tap navigates straight to that release moment.
 *   • Click anywhere on the track to seek; mouse-drag works while held.
 *
 * Self-contained dragging: we attach window-level mousemove / mouseup
 * during a drag so the seek tracks the cursor even if it leaves the
 * track horizontally.
 */
function VideoTimelineScrubber({ currentTime, duration, inS, outS, markers, window, onSeek }) {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);

  // The scrubber operates in a viewport [windowStart, windowEnd]. By default
  // that's the full match; when the coach expands a clip row we narrow it to
  // [clip.start_s, clip.end_s] so a 15-second clip's markers spread across
  // the full pixel width instead of collapsing to <1% of a 41-minute timeline.
  const windowStart = window?.start ?? 0;
  const windowEnd   = window?.end   ?? duration;
  const windowSpan  = Math.max(0.001, windowEnd - windowStart);

  const ratioOf = (t) => Math.max(0, Math.min(1, (t - windowStart) / windowSpan));
  const seekFromClientX = (clientX) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    onSeek(windowStart + ratio * windowSpan);
  };

  const onMouseDown = (e) => {
    draggingRef.current = true;
    seekFromClientX(e.clientX);
    const onMove = (ev) => { if (draggingRef.current) seekFromClientX(ev.clientX); };
    const onUp = () => {
      draggingRef.current = false;
      globalThis.removeEventListener("mousemove", onMove);
      globalThis.removeEventListener("mouseup", onUp);
    };
    globalThis.addEventListener("mousemove", onMove);
    globalThis.addEventListener("mouseup", onUp);
  };

  const onMarkerClick = (e, t) => {
    e.stopPropagation();
    onSeek(t);
  };

  // Markers visible in the current viewport — when zoomed we hide ticks
  // that belong to OTHER clips so the focused clip's markers can breathe.
  const visibleMarkers = window?.zoomed
    ? markers.filter((m) => m.t >= windowStart && m.t <= windowEnd)
    : markers;

  const cutLeftPct  = ratioOf(Math.min(inS, outS)) * 100;
  const cutRightPct = ratioOf(Math.max(inS, outS)) * 100;
  const playedPct   = ratioOf(currentTime) * 100;
  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const r = (s % 60).toFixed(2);
    return `${m}:${r.padStart(5, "0")}`;
  };

  return (
    <div
      data-testid="video-timeline-scrubber"
      data-zoomed={window?.zoomed ? "true" : "false"}
      className={`border bg-card rounded-sm p-3 space-y-2 transition-colors ${
        window?.zoomed ? "border-primary/60" : "border-border"
      }`}
    >
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest">
        <span className={`flex items-center gap-2 ${window?.zoomed ? "text-primary" : "text-muted-foreground"}`}>
          Timeline
          {window?.zoomed && (
            <span
              data-testid="scrubber-zoom-label"
              className="text-primary font-bold normal-case tracking-normal"
            >
              · zoomed to {window.label} · {fmtTime(windowStart)} → {fmtTime(windowEnd)}
            </span>
          )}
        </span>
        <span className="text-foreground tabular-nums">
          {visibleMarkers.length > 0
            ? `${visibleMarkers.length} pass marker${visibleMarkers.length === 1 ? "" : "s"}`
            : "no AI passes yet"}
        </span>
      </div>
      <div
        ref={trackRef}
        onMouseDown={onMouseDown}
        className="relative h-3 bg-secondary rounded-full cursor-pointer select-none group"
        role="slider"
        aria-valuemin={windowStart} aria-valuemax={windowEnd} aria-valuenow={currentTime}
      >
        {/* Cut window (amber band) — only visible when in/out have been set. */}
        {cutRightPct > cutLeftPct && (
          <div
            data-testid="scrubber-cut-window"
            className="absolute top-0 h-full bg-amber-300/30 border-x border-amber-300/60 pointer-events-none"
            style={{ left: `${cutLeftPct}%`, width: `${cutRightPct - cutLeftPct}%` }}
          />
        )}
        {/* Played portion (primary green-cyan). */}
        <div
          className="absolute top-0 left-0 h-full bg-primary rounded-l-full pointer-events-none"
          style={{ width: `${playedPct}%` }}
        />
        {/* Handle (circular thumb). */}
        <div
          data-testid="scrubber-thumb"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-primary border-2 border-background shadow-md pointer-events-none group-hover:scale-110 transition-transform"
          style={{ left: `${playedPct}%` }}
        />
        {/* Pass markers (thin pink ticks) — clickable. */}
        {visibleMarkers.map((m) => {
          const tone = m.status === "verified" || m.status === "corrected"
            ? "bg-emerald-300 hover:bg-emerald-200"
            : m.status === "rejected"
              ? "bg-rose-300/40 hover:bg-rose-200"
              : "bg-pink-400 hover:bg-pink-300";
          return (
            <button
              key={m.id}
              data-testid={`scrubber-pass-marker-${m.id}`}
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => onMarkerClick(e, m.t)}
              title={`${m.title} — jump to ${m.t.toFixed(2)} s`}
              className={`absolute top-[-3px] h-[18px] w-0.5 ${tone} pointer-events-auto cursor-pointer rounded-full`}
              style={{ left: `${ratioOf(m.t) * 100}%` }}
            />
          );
        })}
      </div>
    </div>
  );
}


function Step({ n, t, body }) {
  return (
    <li className="flex gap-3">
      <span className="w-5 h-5 shrink-0 rounded-sm bg-accent text-primary font-mono text-[10px] font-bold flex items-center justify-center mt-0.5">
        {n}
      </span>
      <div>
        <div className="text-foreground font-medium text-sm">{t}</div>
        <div className="text-xs text-muted-foreground leading-snug">{body}</div>
      </div>
    </li>
  );
}

function NextStepHint({ clips }) {
  if (!clips || clips.length === 0) return null;

  // status is "tracked" iff tracking ran (we lose tracking_summary in summary endpoint)
  const draftCount = clips.filter((c) => c.status === "draft").length;
  const labeledNotTracked = clips.filter((c) => c.status === "labeled").length;
  const trackedNotLabeled = clips.filter((c) => c.status === "tracked").length;
  const exported = clips.filter((c) => c.status === "exported").length;
  const tracking = clips.some((c) => c.status === "tracking");
  const needTrack = draftCount + labeledNotTracked;

  let icon = "▸";
  let title = "";
  let body = "";

  if (tracking) {
    icon = "⏳";
    title = "AI tracking in progress";
    body = "YOLOv8x + ByteTRACK is running on one of your sequences. You can keep cutting more sequences while the job runs — they'll queue automatically on a single CPU worker.";
  } else if (needTrack > 0) {
    icon = "⚡";
    title = `Next: run AI tracking on ${needTrack} ${needTrack === 1 ? "sequence" : "sequences"}`;
    body = labeledNotTracked > 0
      ? `${labeledNotTracked} sequence${labeledNotTracked === 1 ? "" : "s"} ${labeledNotTracked === 1 ? "has" : "have"} labels but no tracking yet. Click the green Track button on each row (or inside the expanded form) — ~30–90 s per cut on CPU.`
      : "Click the green Track button on each Draft row to run YOLOv8x + ByteTRACK. ~30–90 s per cut on CPU. You can also label first and track later — both orders work.";
  } else if (trackedNotLabeled > 0) {
    icon = "✎";
    title = `Next: add tactical labels to ${trackedNotLabeled} tracked ${trackedNotLabeled === 1 ? "sequence" : "sequences"}`;
    body = "Click the ⌄ icon on a row to open its annotation form. Each label parameter has a ? hover for guidance. Save when done.";
  } else if (exported > 0 && exported === clips.length) {
    icon = "✓";
    title = "All sequences exported";
    body = "Match fully processed. Re-download anytime, or hit Finish & Return to close this match.";
  } else {
    return null;
  }

  return (
    <div
      data-testid="next-step-hint"
      className="border border-primary/40 bg-accent/40 rounded-sm px-4 py-3 mb-3 flex items-start gap-3"
    >
      <div className="font-display font-black text-primary text-lg leading-none mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-0.5">
          {title}
        </div>
        <div className="text-xs text-foreground/80 leading-relaxed">
          {body}
        </div>
      </div>
    </div>
  );
}

function ClipRow({ clip, parent, index, duration, isMixed, matchFormat, matchDivision, expanded, onExpand, onDelete, onSeek, onRefresh, getVideoTime, currentTime, onNudgeFrame }) {
  const [job, setJob] = useState(clip.job ? { status: clip.status, progress: 1, message: "done" } : null);
  const [polling, setPolling] = useState(clip.status === "tracking");
  const pollRef = useRef(null);
  // Per-clip Pass-Vector inline editor toggle.
  const [pvOpen, setPvOpen] = useState(false);
  // Phase F · Async-Job polling state for the Re-Render MP4 button —
  // surfaces `progress_pct + message + elapsed_s` from the backend
  // job document straight into the button label + a tiny under-fill
  // progress bar.
  const [rerender, setRerender] = useState({ busy: false, pct: 0, message: "", elapsedSec: 0 });
  const hasPV = !!clip.pass_vector;
  // 🆕 Broadcast-Workflow: „kalibriert" = Basis-H ODER irgendein
  // joint-Anker mit eigener Homographie (frei gezeichnet / dl_auto).
  const parentCalibrated = !!(parent?.calibration?.homography)
    || ((parent?.calibration?.extra_zones || []).some(
      (z) => z?.status === "joint" && z?.homography,
    ));

  const status = job?.status === "running" ? "tracking" : clip.status;
  const running = status === "tracking";

  useEffect(() => {
    if (!polling) return;
    pollRef.current = setInterval(async () => {
      try {
        const j = await getClipJob(clip.id);
        setJob(j);
        if (j.status === "completed") {
          clearInterval(pollRef.current); setPolling(false);
          toast.success(`${clip.name} · tracking complete`);
          onRefresh();
        } else if (j.status === "failed") {
          clearInterval(pollRef.current); setPolling(false);
          toast.error(`${clip.name}: ${j.error || "tracking failed"}`);
          onRefresh();
        }
      } catch (_) { /* ignore */ }
    }, 1500);
    return () => clearInterval(pollRef.current);
  }, [polling, clip.id, clip.name, onRefresh]);

  const track = async () => {
    try {
      await startClipTracking(clip.id);
      setJob({ status: "running", progress: 0, message: "queued" });
      setPolling(true);
      toast.success(`${clip.name} · queued for tracking`);
    } catch (err) {
      toast.error(fehlerText(err) || "Could not start tracking");
    }
  };

  const triggerDownload = async (kind) => {
    const url = clipExportUrl(clip.id, kind);
    const filename = `seq_${clip.id.slice(0, 8)}.${kind}`;
    const tid = toast.loading(`Preparing ${kind.toUpperCase()}…${kind === "mp4" ? " (may take 5–10 s for the first time)" : ""}`);
    try {
      await downloadFile(url, filename);
      toast.success(`Downloaded ${filename}`, { id: tid });
    } catch (err) {
      toast.error(err.message || "Download failed", { id: tid });
    }
  };

  return (
    <div
      data-testid={`clip-row-${clip.id}`}
      className={`border bg-card rounded-sm overflow-hidden transition-colors ${expanded ? "border-primary/50" : "border-border"}`}
    >
      {/* Row header */}
      <div className="flex items-center gap-3 p-3">
        <div className="w-7 h-7 rounded-sm bg-accent border border-border flex items-center justify-center font-mono text-xs text-primary">
          {index}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm">{clip.name}</div>
          <div className="font-mono text-[10px] text-muted-foreground tabular-nums">
            {fmtTime(clip.start_s)} → {fmtTime(clip.end_s)} · {clip.duration_s.toFixed(1)}s
            {clip.offense && <span className="ml-2 text-foreground">▸ {clip.offense}</span>}
            {clip.defense && <span className="ml-1 text-foreground">vs {clip.defense}</span>}
          </div>
        </div>

        <Button
          size="sm" variant="ghost"
          onClick={() => onSeek(clip.start_s)}
          className="h-8 px-2 font-mono text-[10px] hover:bg-accent hover:text-primary"
        >
          Jump
        </Button>

        <StatusPill status={status} />

        {/* Pass-Vector quick-toggle — opens the 2D-Taktikboard inline below.
            🆕 Broadcast-Workflow: IMMER sichtbar (ab TRACKED nutzbar) —
            ohne jegliche Homographie zeigt der Klick einen Hinweis statt
            den Button zu verstecken. */}
        <Button
          size="sm" variant="outline"
          data-testid={`clip-pass-vector-toggle-${clip.id}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!parentCalibrated) {
              toast.warning(
                "Keine Kalibrierung — im Anker-Sammelmodus 2–3 sichtbare Feldlinien frei nachziehen (oder Auto-Kalibrierung nutzen)",
                { duration: 8000 },
              );
              return;
            }
            // Make sure the body is open so the editor renders below.
            if (!expanded) onExpand?.();
            setPvOpen((v) => !v);
          }}
          className={`h-7 px-2 font-display font-bold uppercase tracking-widest text-[10px] ${
            hasPV
              ? "border-pink-400 bg-pink-400/15 text-pink-300 hover:bg-pink-400 hover:text-black"
              : "border-pink-400/40 bg-pink-400/5 text-pink-300/70 hover:bg-pink-400/10"
          } ${pvOpen ? "ring-2 ring-pink-400/40" : ""}`}
          title={hasPV ? `Edit pass · ${clip.pass_vector?.length_m?.toFixed(1)} m` : "Draw release → catch on the calibration frame"}
        >
          <ArrowRightCircle className="w-3.5 h-3.5 mr-1" />
          {hasPV
            ? `Pass ${clip.pass_vector?.length_m?.toFixed(1)} m`
            : "Edit Passes"}
        </Button>

        {(clip.status === "draft" || clip.status === "labeled" || clip.status === "failed") && (
          <Button
            data-testid={`track-clip-${clip.id}`}
            size="sm" onClick={track}
            disabled={running}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest text-[10px]"
          >
            {running ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Tracking</> : <><Zap className="w-3.5 h-3.5 mr-1" /> Track</>}
          </Button>
        )}

        {(status === "tracked" || status === "labeled" || status === "exported") && (
          <div className="flex items-center gap-1">
            <Button
              data-testid={`download-mp4-${clip.id}`}
              size="sm"
              onClick={() => triggerDownload("mp4")}
              className="bg-primary/15 text-primary hover:bg-primary hover:text-primary-foreground border border-primary/40 font-display font-bold uppercase tracking-widest text-[10px] h-7"
              title="Download annotated MP4 clip (with YOLO bounding boxes)"
            >
              <Film className="w-3.5 h-3.5 mr-1" /> MP4
            </Button>
            <Button
              data-testid={`rerender-mp4-${clip.id}`}
              data-job-progress={rerender.busy ? rerender.pct : undefined}
              data-job-message={rerender.busy ? rerender.message : undefined}
              size="sm" variant="ghost"
              disabled={rerender.busy}
              onClick={async () => {
                setRerender({ busy: true, pct: 0, message: "queued", elapsedSec: 0 });
                const tid = toast.loading("Re-rendering MP4 · queued…");
                try {
                  await rerenderClipMp4(clip.id, {
                    onProgress: ({ progress_pct, message, elapsed_ms }) => {
                      const elapsedSec = Math.round(elapsed_ms / 1000);
                      setRerender({ busy: true, pct: progress_pct, message, elapsedSec });
                      toast.loading(
                        `Rendering video · ${progress_pct}% · ${message} · ${elapsedSec}s`,
                        { id: tid },
                      );
                    },
                  });
                  toast.success("MP4 re-rendered with bounding-box overlays", { id: tid });
                  onRefresh?.();
                } catch (err) {
                  toast.error(err?.detail || err?.message || "Re-render failed", { id: tid });
                } finally {
                  setRerender({ busy: false, pct: 0, message: "", elapsedSec: 0 });
                }
              }}
              className="h-7 px-2 hover:bg-accent hover:text-primary font-mono text-[10px] relative overflow-hidden min-w-[88px]"
              title={
                rerender.busy
                  ? `Rendering · ${rerender.pct}% · ${rerender.message} · ${rerender.elapsedSec}s elapsed`
                  : "Force a fresh server-side render of the MP4 (useful for clips tracked before the box-overlay shipped)"
              }
            >
              {rerender.busy && (
                <span
                  data-testid={`rerender-mp4-progress-${clip.id}`}
                  className="absolute left-0 top-0 h-full bg-primary/20 transition-[width] duration-300"
                  style={{ width: `${rerender.pct}%` }}
                />
              )}
              <span className="relative flex items-center">
                {rerender.busy
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> {rerender.pct}% · {rerender.elapsedSec}s</>
                  : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Re-Render</>}
              </span>
            </Button>
            <ReidStitchButton clip={clip} onUpdate={onRefresh} />
            <Button
              size="sm" variant="ghost"
              onClick={() => triggerDownload("csv")}
              className="h-7 px-2 hover:bg-accent hover:text-primary font-mono text-[10px]"
              title="Download tracking CSV"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => triggerDownload("json")}
              className="h-7 px-2 hover:bg-accent hover:text-primary font-mono text-[10px]"
              title="Download tracking JSON"
            >
              <FileJson className="w-3.5 h-3.5 mr-1" /> JSON
            </Button>
            <Button
              data-testid={`download-zip-${clip.id}`}
              size="sm" variant="ghost"
              onClick={() => triggerDownload("all.zip")}
              className="h-7 px-2 hover:bg-accent hover:text-primary font-mono text-[10px]"
              title="Download MP4 + CSV + JSON as ZIP"
            >
              <Package className="w-3.5 h-3.5 mr-1" /> ZIP
            </Button>
          </div>
        )}

        <Button
          size="sm" variant="ghost"
          onClick={onExpand}
          className="h-8 w-8 p-0 hover:bg-accent hover:text-primary"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>

        <Button
          data-testid={`delete-clip-${clip.id}`}
          size="sm" variant="ghost"
          onClick={onDelete}
          title="Delete sequence"
          className="h-8 w-8 p-0 hover:bg-destructive/20 hover:text-destructive"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Job progress (always visible if running) */}
      {running && (
        <div className="px-3 pb-3">
          <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${(job?.progress || 0) * 100}%` }}
            />
          </div>
          <div className="mt-1 font-mono text-[10px] text-muted-foreground truncate">
            ▸ {job?.message || "queued"}
          </div>
        </div>
      )}

      {/* Expanded body (annotation form) */}
      {expanded && (
        <div className="border-t border-border bg-secondary/30 p-5">
          {!clip.tracking_summary && (
            <div className="mb-4 border border-primary/40 bg-accent/40 rounded-sm p-4 flex items-center gap-3">
              <Zap className="w-5 h-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-0.5">
                  AI Tracking not run yet
                </div>
                <div className="text-xs text-foreground/80">
                  Run YOLOv8x + ByteTRACK now to generate player positions and speeds for this sequence. You can also save labels first and track later.
                </div>
              </div>
              <Button
                onClick={track}
                disabled={running}
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest"
              >
                {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Tracking…</> : <><Zap className="w-4 h-4 mr-2" /> Run Tracking</>}
              </Button>
            </div>
          )}
          {pvOpen && parentCalibrated && (
            <div className="mb-4">
              <ClipPassVectorEditor
                clip={clip}
                parent={parent}
                onSaved={onRefresh}
                onClose={() => setPvOpen(false)}
                getVideoTime={getVideoTime}
              />
            </div>
          )}
          {parentCalibrated && (
            <div className="mb-4">
              <PassVectorTriage
                clip={clip}
                parent={parent}
                onMutated={onRefresh}
                getVideoTime={getVideoTime}
                currentTime={currentTime}
                onNudgeFrame={onNudgeFrame}
                onJumpToRelease={(clipLocalSeconds) =>
                  // Translate the vector's clip-local timestamp into the
                  // full-match video's source-time and seek + scroll up to
                  // the player. Without the scroll the coach would have to
                  // hunt for the video element on a long page.
                  onSeek((clip.start_s || 0) + clipLocalSeconds, { scroll: true })
                }
              />
            </div>
          )}
          <AnnotationForm
            clip={clip}
            isMixed={isMixed}
            matchFormat={matchFormat}
            matchDivision={matchDivision}
            duration={duration}
            onSaved={onRefresh}
          />
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: { c: "bg-secondary text-muted-foreground border-border", l: "Draft" },
    tracking: { c: "bg-primary/15 text-primary border-primary/30 animate-pulse", l: "Tracking" },
    tracked: { c: "bg-primary/15 text-primary border-primary/30", l: "Tracked" },
    labeled: { c: "bg-primary/15 text-primary border-primary/30", l: "Labeled" },
    exported: { c: "bg-primary text-primary-foreground border-primary", l: "Exported" },
    failed: { c: "bg-destructive/15 text-destructive border-destructive/30", l: "Failed" },
  };
  const { c, l } = map[status] || map.draft;
  return (
    <Badge className={`${c} border font-mono text-[10px] uppercase tracking-widest`}>
      {l}
    </Badge>
  );
}

function AnnotationForm({ clip, isMixed, matchFormat, matchDivision, duration, onSaved }) {
  const init = clip.labels || {};
  const splitOptions = SPLITS_BY_FORMAT[matchFormat] || SPLITS_BY_FORMAT.Outdoor;

  const [name, setName] = useState(clip.name || "");
  const [startS, setStartS] = useState(clip.start_s);
  const [endS, setEndS] = useState(clip.end_s);
  const [genderSplit, setGenderSplit] = useState(init.gender_split || "");
  const [offense, setOffense] = useState(init.offense_system || "");
  const [defense, setDefense] = useState(init.defense_system || "");
  const [phase, setPhase] = useState(init.game_phase || "");
  const [quality, setQuality] = useState(init.quality || "");
  const [difficulty, setDifficulty] = useState(init.difficulty || "");
  const [note, setNote] = useState(init.expert_note || "");
  const [tags, setTags] = useState(init.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  };
  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const save = async () => {
    try {
      setSaving(true);
      // Update name/range if changed
      if (name !== clip.name || startS !== clip.start_s || endS !== clip.end_s) {
        await updateClip(clip.id, { name, start_s: startS, end_s: endS });
      }
      await setClipLabels(clip.id, {
        gender_split: isMixed ? (genderSplit || null) : null,
        offense_system: offense || null,
        defense_system: defense || null,
        game_phase: phase || null,
        quality: quality || null,
        difficulty: difficulty || null,
        expert_note: note || null,
        tags,
      });
      toast.success(`${clip.name} · labels saved`);
      onSaved();
    } catch (err) {
      toast.error(fehlerText(err) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-4">
        {/* Range editor */}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-card border-border focus:border-primary"
            />
          </Field>
          <Field label="Start (s)">
            <Input
              type="number" step="0.1" min="0" max={duration}
              value={startS}
              onChange={(e) => setStartS(parseFloat(e.target.value) || 0)}
              className="bg-card border-border focus:border-primary font-mono"
            />
          </Field>
          <Field label="End (s)">
            <Input
              type="number" step="0.1" min="0" max={duration}
              value={endS}
              onChange={(e) => setEndS(parseFloat(e.target.value) || 0)}
              className="bg-card border-border focus:border-primary font-mono"
            />
          </Field>
        </div>

        {/* Label form */}
        <div className="grid sm:grid-cols-2 gap-4">
          {/* ── Division / Gender-Split (per-sequence) ──────────────────
              In Mixed division the on-field gender ratio varies per point
              (3M/2W, 2M/3W …) so we expose it as a dropdown. For Open /
              Women the ratio is fixed by the rules and inherited from the
              match-level division — we still show a read-only badge so
              the field never silently disappears (regression that bit us
              once and confused admins working on non-Mixed matches). */}
          <Field
            label={isMixed ? `Gender Split · ${matchFormat}` : "Division"}
            paramKey="gender_split"
          >
            {isMixed ? (
              <Select value={genderSplit} onValueChange={setGenderSplit}>
                <SelectTrigger data-testid={TID.cfgSplit} className="bg-card border-primary/40">
                  <SelectValue placeholder="Split for this sequence" />
                </SelectTrigger>
                <SelectContent>
                  {splitOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div
                data-testid={`${TID.cfgSplit}-readonly`}
                className="h-10 flex items-center px-3 rounded-sm bg-card border border-border font-mono text-xs uppercase tracking-widest text-muted-foreground"
                title="Division is set at the match level. Per-sequence splits only exist for Mixed."
              >
                <span className="text-primary mr-2">
                  {matchDivision || "—"}
                </span>
                <span className="text-[10px]">· per-sequence split N/A</span>
              </div>
            )}
          </Field>
          <Field label="Offense System" paramKey="offense_system">
            <Select value={offense} onValueChange={setOffense}>
              <SelectTrigger data-testid={TID.labelOffense} className="bg-card border-border">
                <SelectValue placeholder="Select system" />
              </SelectTrigger>
              <SelectContent>
                {OFFENSE.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Defense System" paramKey="defense_system">
            <Select value={defense} onValueChange={setDefense}>
              <SelectTrigger data-testid={TID.labelDefense} className="bg-card border-border">
                <SelectValue placeholder="Select system" />
              </SelectTrigger>
              <SelectContent>
                {DEFENSE.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Game Phase" paramKey="game_phase">
            <Select value={phase} onValueChange={setPhase}>
              <SelectTrigger data-testid={TID.labelPhase} className="bg-card border-border">
                <SelectValue placeholder="Select phase" />
              </SelectTrigger>
              <SelectContent>
                {PHASE.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Quality" paramKey="quality">
            <Select value={quality} onValueChange={setQuality}>
              <SelectTrigger data-testid={TID.labelQuality} className="bg-card border-border">
                <SelectValue placeholder="Quality" />
              </SelectTrigger>
              <SelectContent>
                {QUALITY.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Difficulty" paramKey="difficulty">
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger data-testid={TID.labelDifficulty} className="bg-card border-border">
                <SelectValue placeholder="Difficulty" />
              </SelectTrigger>
              <SelectContent>
                {DIFFICULTY.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tags" paramKey="tags">
            <div className="flex gap-2">
              <Input
                data-testid={TID.labelTagsInput}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                placeholder="huck, layout, break…"
                className="bg-card border-border focus:border-primary"
              />
              <Button type="button" variant="outline" onClick={addTag} className="border-border">
                <Tag className="w-4 h-4" />
              </Button>
            </div>
          </Field>
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <Badge key={t}
                className="bg-accent text-primary hover:bg-accent/80 border border-primary/30 font-mono uppercase tracking-wider text-[10px] gap-1.5 pr-1.5"
              >
                {t}
                <button onClick={() => removeTag(t)} className="hover:bg-primary/20 rounded-sm w-4 h-4 flex items-center justify-center">
                  <XIcon className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        <Field label="Expert Note" paramKey="expert_note">
          <Textarea
            data-testid={TID.labelNote}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Positioning, decision quality, key cuts, errors…"
            className="bg-card border-border focus:border-primary"
          />
        </Field>

        <Button
          data-testid={TID.saveLabelsBtn}
          onClick={save}
          disabled={saving}
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Sequence"}
        </Button>
      </div>

      {/* Side stats */}
      <div className="space-y-4">
        {clip.tracking_summary && (
          <div className="border border-primary/40 bg-accent/30 rounded-sm p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-3 flex items-center gap-1.5">
              <Film className="w-3 h-3" /> Downloads
            </div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={async () => {
                  const tid = toast.loading("Preparing MP4… (may take 5–10 s)");
                  try {
                    await downloadFile(clipExportUrl(clip.id, "mp4"), `seq_${clip.id.slice(0, 8)}.mp4`);
                    toast.success("MP4 downloaded", { id: tid });
                  } catch (e) { toast.error("Download failed", { id: tid }); }
                }}
                data-testid={`form-download-mp4-${clip.id}`}
                className="w-full flex items-center gap-2.5 px-3 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-display font-bold uppercase tracking-widest text-xs transition-colors"
              >
                <Film className="w-4 h-4" />
                <span className="flex-1 text-left">Download MP4 Clip</span>
                <Download className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await downloadFile(clipExportUrl(clip.id, "csv"), `seq_${clip.id.slice(0, 8)}.csv`);
                    toast.success("CSV downloaded");
                  } catch (e) { toast.error("Download failed"); }
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 bg-secondary hover:bg-accent text-foreground hover:text-primary border border-border rounded-sm font-mono text-xs transition-colors"
              >
                <FileSpreadsheet className="w-4 h-4 text-primary" />
                <span className="flex-1 text-left">Tracking CSV</span>
                <Download className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await downloadFile(clipExportUrl(clip.id, "json"), `seq_${clip.id.slice(0, 8)}.json`);
                    toast.success("JSON downloaded");
                  } catch (e) { toast.error("Download failed"); }
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 bg-secondary hover:bg-accent text-foreground hover:text-primary border border-border rounded-sm font-mono text-xs transition-colors"
              >
                <FileJson className="w-4 h-4 text-primary" />
                <span className="flex-1 text-left">Full Bundle JSON</span>
                <Download className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                data-testid={`form-download-zip-${clip.id}`}
                onClick={async () => {
                  const tid = toast.loading("Bundling MP4 + CSV + JSON…");
                  try {
                    await downloadFile(clipExportUrl(clip.id, "all.zip"), `seq_${clip.id.slice(0, 8)}.zip`);
                    toast.success("Bundle downloaded", { id: tid });
                  } catch (e) { toast.error("Bundle download failed", { id: tid }); }
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 bg-accent border border-primary/40 hover:bg-primary hover:text-primary-foreground text-primary rounded-sm font-display font-bold uppercase tracking-widest text-xs transition-colors mt-1"
              >
                <Package className="w-4 h-4" />
                <span className="flex-1 text-left">All-in-One ZIP</span>
                <Download className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        <div className="border border-border bg-card rounded-sm p-4 h-fit">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
            Tracking Summary
          </div>
          {clip.tracking_summary ? (
            <dl className="space-y-2 font-mono text-xs">
              <Row label="Players" value={zahl(clip.tracking_summary.num_players)} />
              <Row label="Frames" value={zahl(clip.tracking_summary.num_frames)} />
              <Row label="Detections" value={zahl(clip.tracking_summary.num_rows)} />
              <Row label="Avg Speed" value={tempo(clip.tracking_summary.avg_speed_kmh)} />
              <Row label="Peak (p95)" value={tempo(clip.tracking_summary.speed_p95_kmh)} />
            </dl>
          ) : (
            <div className="text-muted-foreground text-sm">No tracking data yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, paramKey, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <UILabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {label}
        </UILabel>
        {paramKey && <ParamHelp paramKey={paramKey} />}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-border/50 pb-1.5">
      <span className="text-muted-foreground uppercase tracking-widest text-[10px]">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}
